<template>
  <button ref="el" :class="classes" :disabled="isDisabled" :style="{ backgroundColor }" tabindex="0" type="button" @click="onClickDebounced">
    <VLoadingDots v-if="loading && !loadingLabel" mask mask-color="rgba(0,0,0,0)" :size="5" />

    <VIcon v-if="icon && iconLeft" class="v-btn__icon" :icon :size="_iconSize" :type="iconType" />

    <span v-if="loading && loadingLabel" class="v-btn__label v-btn__loading-label">{{ loadingLabel }}</span>

    <span v-if="label || $slots.default" class="v-btn__label">
      <slot>{{ label }}</slot>
    </span>

    <VIcon v-if="icon && !iconLeft" class="v-btn__icon" :icon :size="_iconSize" :type="iconType" />

    <VIconHelp v-if="_help" class="v-btn__icon" :help="_help" />
  </button>
</template>

<script setup lang="ts">
import { debounce as _debounce, type DebounceSettings } from 'lodash-es'
import { computed, inject, useSlots, useTemplateRef } from 'vue'

import type { VButtonSize, VButtonType, VIconIcon, VIconType } from '../types.ts'
import { DisabledHelp } from '../utils/disabledHelp.ts'
import { $formKey } from '../utils/injections.ts'
import VIcon from './Icon/VIcon.vue'
import VIconHelp from './VIconHelp.vue'
import VLoadingDots from './VLoadingDots.vue'

export interface DebounceOptions {
  wait: number
  options: DebounceSettings
}

export interface Props {
  /**
   * Allows you to provide a custom background color.
   */
  backgroundColor?: string

  /**
   * Indicates if the button state should be disabled. Clicks will not be emitted.
   */
  disabled?: boolean | null

  /**
   * Displays one or more help messages in a tooltip by hovering over a help icon inside the button.
   */
  help?: string | string[]

  /**
   * If you want to disable the button and show a help icon explaining why it is disabled.
   * You can provide a message as key and a truthy/falsy value to indicate if it should be disabled with that message.
   */
  disabledHelp?: Record<string, any>

  /**
   * Allows you to set a different button type when hovering over the button.
   */
  hoverType?: VButtonType

  /**
   * Displays an icon inside the button.
   */
  icon?: VIconIcon | ''

  /**
   * If true, places the icon on the left side of the text, otherwise on the right side.
   */
  iconLeft?: boolean

  /**
   * The icon size.
   */
  iconSize?: number

  /**
   * The `type` prop of `VIcon` when an icon is provided.
   */
  iconType?: VIconType

  /**
   * The text to display inside the button.
   */
  label?: string

  /**
   * Replaces the button text with a loading indicator or with `loadingLabel`.
   */
  loading?: boolean

  /**
   * The text that should be displayed when loading.
   */
  loadingLabel?: string | null

  /**
   * If true, the button will not have a background color but it will show a background color when hovering over it.
   */
  plain?: boolean

  /**
   * If true, the button will have a pulsing effect.
   */
  pulse?: boolean

  /**
   * If true, the button will be rounded.
   */
  rounded?: boolean

  /**
   * The size of the button.
   */
  size?: VButtonSize

  /**
   * If this button is placed in a `VForm` it will act as a form submit button.
   */
  submit?: boolean

  /**
   * Changes the look of the button.
   */
  type?: VButtonType

  /**
   * Allows you to specify options for the debouncing behavior.
   * `false` means no debounce.
   */
  debounce?: false | DebounceOptions
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  type: 'primary',
  hoverType: '',
  plain: false,
  rounded: false,
  size: 'medium',
  backgroundColor: undefined,
  icon: undefined,
  iconSize: 0,
  iconType: 'primary',
  loadingLabel: null,
  help: '',
  disabled: null,
  disabledHelp: undefined,
  debounce: () => ({ wait: 300, options: { leading: true, trailing: false } }),
})

const emit = defineEmits<{
  click: [value: MouseEvent]
}>()

defineSlots<{
  default?(): any
}>()

const form = inject($formKey, undefined)
const slots = useSlots()

const disabledHelp = computed(() => (props.disabledHelp ? new DisabledHelp(props.disabledHelp) : undefined))
const _help = computed(() => props.help || disabledHelp.value?.help)

const isDisabled = computed((): boolean => {
  if (props.disabled !== null || disabledHelp.value?.disabled !== undefined) {
    if (props.disabled) {
      return true
    }

    if (disabledHelp.value?.disabled) {
      return true
    }

    return false
  }

  return form?.props?.disabled ?? false
})

const classes = computed(() => ({
  'v-btn': true,
  [`v-btn--${props.type}`]: props.type,
  [`v-btn--hover-${props.hoverType}`]: props.hoverType,
  'v-btn--plain': props.plain,
  'v-btn--rounded': props.rounded,
  [`v-btn--${props.size}`]: true,
  'v-btn--loading': props.loading,
  'v-btn--pulse': props.pulse,
}))

const _iconSize = computed(() => {
  if (props.iconSize) return props.iconSize
  if (!props.label && !slots.default) return 12

  return {
    mini: 10,
    small: 10,
    medium: 12,
    large: 12,
  }[props.size]
})

const el = useTemplateRef('el')

function onClick(event: MouseEvent) {
  el.value?.blur()

  if (isDisabled.value || props.loading) {
    return
  }

  emit('click', event)

  if (props.submit) {
    form?.submit()
  }
}

const onClickDebounced = _debounce(onClick, props.debounce ? props.debounce.wait : 0, props.debounce ? props.debounce.options : { leading: true, trailing: false })

defineExpose({
  _iconSize,
})
</script>

<style>
.v-btn {
  position: relative;
  border: 1px solid #fff;
  background-color: #ccc;
  color: var(--color-text-light);
  border-radius: var(--border-radius);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  line-height: 34px;
  font-size: 16px;
  outline: none;
  overflow: hidden;
  transition:
    opacity var(--transition),
    background-color var(--transition),
    color var(--transition),
    border var(--transition);
  cursor: pointer;
  flex-shrink: 0;
}

.v-btn.v-btn--pulse,
.v-btn.v-btn--loading {
  transition:
    opacity var(--transition),
    background-color var(--transition),
    color var(--transition),
    border var(--transition),
    box-shadow 0.1s ease;
}

.v-btn:disabled {
  cursor: default;
  border-color: var(--color-grey-20);
  background-color: var(--color-grey-20);
  color: var(--color-text-light);
}

.v-btn:disabled.v-btn--white {
  opacity: 0.5;
}

.v-btn:disabled .v-btn__icon * {
  stroke: var(--color-text-light);
}

.v-btn:disabled .v-btn__icon *.v-icon-fill-color {
  fill: var(--color-text-light);
}

.v-btn:focus:not(:active, .v-btn--link) {
  box-shadow: var(--box-shadow-light);
}

.v-btn.v-btn--link:focus:not(:active) .v-btn__label {
  text-decoration: underline;
}

.v-btn:not(:last-of-type) {
  margin-right: 14px;
}

.v-btn .v-btn__label a {
  color: unset;
}

.v-btn .v-btn__label a:hover {
  text-decoration: unset;
}

.v-btn span + .v-btn__icon,
.v-btn .v-btn__icon + span {
  margin-left: 5px;
}

.v-btn .v-btn__icon.v-icon-help {
  margin-left: 10px;
}

.v-btn--mini {
  height: 18px;
  min-width: 18px;
  padding: 0 3px;
  font-size: 12px;
}

.v-btn--mini .v-btn__icon.v-icon-plus *,
.v-btn--mini .v-btn__icon.v-icon-minus * {
  stroke-width: 1.5;
}

.v-btn--small {
  height: 26px;
  min-width: 26px;
  padding: 0 6px;
  font-size: 14px;
}

.v-btn--small .v-btn__icon.v-icon-plus *,
.v-btn--small .v-btn__icon.v-icon-minus * {
  stroke-width: 1.5;
}

.v-btn--small .v-btn__icon.v-icon-close * {
  stroke-width: 1;
}

.v-btn--medium {
  height: 34px;
  min-width: 34px;
  padding: 0 10px;
  font-size: 16px;
}

.v-btn--large {
  height: 40px;
  min-width: 40px;
  font-size: 20px;
  padding: 0 19px;
}

.v-btn--loading {
  cursor: default;
  user-select: none;
}

.v-btn__loading-label {
  position: absolute;
  text-align: center;
  opacity: 0.6;
}

.v-btn--primary {
  background-color: var(--color-primary);
  border-color: var(--color-primary);
}

.v-btn--primary:not(:disabled) .v-btn__label {
  color: var(--color-text-light);
}

.v-btn--primary:not(:disabled):not(.v-btn--loading, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover {
  background-color: var(--color-secondary);
  border-color: var(--color-secondary);
}

.v-btn--primary:not(:disabled):not(.v-btn--loading):active {
  box-shadow: inset 0 0 6px 0px var(--color-primary);
}

.v-btn--primary.v-btn--loading .v-btn__label:not(.v-btn__loading-label) {
  color: transparent;
}

.v-btn--primary.v-btn--loading .v-btn__icon * {
  stroke: transparent;
}

.v-btn--primary .v-loading-dots > div {
  background-color: var(--color-text-light);
}

.v-btn--primary .v-btn__icon * {
  stroke: var(--color-text-light);
}

.v-btn--hover-primary:hover:not(.v-btn--loading, :disabled) {
  background-color: var(--color-primary);
  border-color: var(--color-primary);
}

.v-btn--hover-primary:hover:not(.v-btn--loading, :disabled) .v-btn__label {
  color: var(--color-text-light);
}

.v-btn--hover-primary:hover:not(.v-btn--loading, :disabled) .v-btn__icon * {
  stroke: var(--color-text-light);
}

.v-btn--secondary {
  background-color: var(--color-blue-3);
  border-color: var(--color-blue-3);
}

.v-btn--secondary:not(:disabled) .v-btn__label {
  color: var(--color-primary);
}

.v-btn--secondary:not(:disabled):not(.v-btn--loading, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover {
  background-color: var(--color-blue-5);
  border-color: var(--color-blue-5);
}

.v-btn--secondary:not(:disabled):not(.v-btn--loading):active {
  box-shadow: inset 0 0 6px 0px rgb(0 0 0 / 10%);
}

.v-btn--secondary.v-btn--loading .v-btn__label:not(.v-btn__loading-label) {
  color: transparent;
}

.v-btn--secondary.v-btn--loading .v-btn__icon * {
  stroke: transparent;
}

.v-btn--secondary .v-loading-dots > div {
  background-color: var(--color-primary);
}

.v-btn--secondary .v-btn__icon * {
  stroke: var(--color-primary);
}

.v-btn--hover-secondary:hover:not(.v-btn--loading, :disabled) {
  background-color: var(--color-blue-3);
  border-color: var(--color-blue-3);
}

.v-btn--hover-secondary:hover:not(.v-btn--loading, :disabled) .v-btn__label {
  color: var(--color-primary);
}

.v-btn--hover-secondary:hover:not(.v-btn--loading, :disabled) .v-btn__icon * {
  stroke: var(--color-primary);
}

.v-btn--tertiary {
  background-color: var(--color-tertiary);
  border-color: var(--color-tertiary);
}

.v-btn--tertiary:not(:disabled) .v-btn__label {
  color: var(--color-text-primary);
}

.v-btn--tertiary:not(:disabled):not(.v-btn--loading, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover {
  background-color: var(--color-tertiary);
  border-color: var(--color-tertiary);
}

.v-btn--tertiary:not(:disabled):not(.v-btn--loading):active {
  box-shadow: inset 0 0 6px 0px rgb(0 0 0 / 10%);
}

.v-btn--tertiary.v-btn--loading .v-btn__label:not(.v-btn__loading-label) {
  color: transparent;
}

.v-btn--tertiary.v-btn--loading .v-btn__icon * {
  stroke: transparent;
}

.v-btn--tertiary .v-loading-dots > div {
  background-color: var(--color-text-primary);
}

.v-btn--tertiary .v-btn__icon * {
  stroke: var(--color-text-primary);
}

.v-btn--hover-tertiary:hover:not(.v-btn--loading, :disabled) {
  background-color: var(--color-tertiary);
  border-color: var(--color-tertiary);
}

.v-btn--hover-tertiary:hover:not(.v-btn--loading, :disabled) .v-btn__label {
  color: var(--color-text-primary);
}

.v-btn--hover-tertiary:hover:not(.v-btn--loading, :disabled) .v-btn__icon * {
  stroke: var(--color-text-primary);
}

.v-btn--quaternary {
  background-color: var(--color-secondary);
  border-color: var(--color-secondary);
}

.v-btn--quaternary:not(:disabled) .v-btn__label {
  color: var(--color-text-light);
}

.v-btn--quaternary:not(:disabled):not(.v-btn--loading, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover {
  background-color: var(--color-primary);
  border-color: var(--color-primary);
}

.v-btn--quaternary:not(:disabled):not(.v-btn--loading):active {
  box-shadow: inset 0 0 6px 0px rgb(0 0 0 / 70%);
}

.v-btn--quaternary.v-btn--loading .v-btn__label:not(.v-btn__loading-label) {
  color: transparent;
}

.v-btn--quaternary.v-btn--loading .v-btn__icon * {
  stroke: transparent;
}

.v-btn--quaternary .v-loading-dots > div {
  background-color: var(--color-text-light);
}

.v-btn--quaternary .v-btn__icon * {
  stroke: var(--color-text-light);
}

.v-btn--hover-quaternary:hover:not(.v-btn--loading, :disabled) {
  background-color: var(--color-secondary);
  border-color: var(--color-secondary);
}

.v-btn--hover-quaternary:hover:not(.v-btn--loading, :disabled) .v-btn__label {
  color: var(--color-text-light);
}

.v-btn--hover-quaternary:hover:not(.v-btn--loading, :disabled) .v-btn__icon * {
  stroke: var(--color-text-light);
}

.v-btn--red {
  background-color: var(--color-red);
  border-color: var(--color-red);
}

.v-btn--red:not(:disabled) .v-btn__label {
  color: var(--color-text-light);
}

.v-btn--red:not(:disabled):not(.v-btn--loading, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover {
  background-color: var(--color-red-dark);
  border-color: var(--color-red-dark);
}

.v-btn--red:not(:disabled):not(.v-btn--loading):active {
  box-shadow: inset 0 0 6px 0px var(--color-red);
}

.v-btn--red.v-btn--loading .v-btn__label:not(.v-btn__loading-label) {
  color: transparent;
}

.v-btn--red.v-btn--loading .v-btn__icon * {
  stroke: transparent;
}

.v-btn--red .v-loading-dots > div {
  background-color: var(--color-text-light);
}

.v-btn--red .v-btn__icon * {
  stroke: var(--color-text-light);
}

.v-btn--hover-red:hover:not(.v-btn--loading, :disabled) {
  background-color: var(--color-red);
  border-color: var(--color-red);
}

.v-btn--hover-red:hover:not(.v-btn--loading, :disabled) .v-btn__label {
  color: var(--color-text-light);
}

.v-btn--hover-red:hover:not(.v-btn--loading, :disabled) .v-btn__icon * {
  stroke: var(--color-text-light);
}

.v-btn--green {
  background-color: var(--color-green);
  border-color: var(--color-green);
}

.v-btn--green:not(:disabled) .v-btn__label {
  color: var(--color-text-light);
}

.v-btn--green:not(:disabled):not(.v-btn--loading, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover {
  background-color: var(--color-green-dark);
  border-color: var(--color-green-dark);
}

.v-btn--green:not(:disabled):not(.v-btn--loading):active {
  box-shadow: inset 0 0 6px 0px var(--color-green);
}

.v-btn--green.v-btn--loading .v-btn__label:not(.v-btn__loading-label) {
  color: transparent;
}

.v-btn--green.v-btn--loading .v-btn__icon * {
  stroke: transparent;
}

.v-btn--green .v-loading-dots > div {
  background-color: var(--color-text-light);
}

.v-btn--green .v-btn__icon * {
  stroke: var(--color-text-light);
}

.v-btn--hover-green:hover:not(.v-btn--loading, :disabled) {
  background-color: var(--color-green);
  border-color: var(--color-green);
}

.v-btn--hover-green:hover:not(.v-btn--loading, :disabled) .v-btn__label {
  color: var(--color-text-light);
}

.v-btn--hover-green:hover:not(.v-btn--loading, :disabled) .v-btn__icon * {
  stroke: var(--color-text-light);
}

.v-btn--white {
  background-color: var(--color-white);
  border-color: var(--color-white);
}

.v-btn--white:not(:disabled) .v-btn__label {
  color: var(--color-text-primary);
}

.v-btn--white:not(:disabled):not(.v-btn--loading, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover {
  background-color: var(--color-grey-8);
  border-color: var(--color-grey-8);
}

.v-btn--white:not(:disabled):not(.v-btn--loading):active {
  box-shadow: inset 0 0 6px 0px var(--color-grey-12);
}

.v-btn--white.v-btn--loading .v-btn__label:not(.v-btn__loading-label) {
  color: transparent;
}

.v-btn--white.v-btn--loading .v-btn__icon * {
  stroke: transparent;
}

.v-btn--white .v-loading-dots > div {
  background-color: var(--color-text-primary);
}

.v-btn--white .v-btn__icon * {
  stroke: var(--color-text-primary);
}

.v-btn--hover-white:hover:not(.v-btn--loading, :disabled) {
  background-color: var(--color-white);
  border-color: var(--color-white);
}

.v-btn--hover-white:hover:not(.v-btn--loading, :disabled) .v-btn__label {
  color: var(--color-text-primary);
}

.v-btn--hover-white:hover:not(.v-btn--loading, :disabled) .v-btn__icon * {
  stroke: var(--color-text-primary);
}

.v-btn--info {
  background-color: var(--color-grey-40);
  border-color: var(--color-grey-40);
}

.v-btn--info:not(:disabled) .v-btn__label {
  color: var(--color-text-light);
}

.v-btn--info:not(:disabled):not(.v-btn--loading, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover {
  background-color: var(--color-grey-20);
  border-color: var(--color-grey-20);
}

.v-btn--info:not(:disabled):not(.v-btn--loading):active {
  box-shadow: inset 0 0 6px 0px var(--color-grey-40);
}

.v-btn--info.v-btn--loading .v-btn__label:not(.v-btn__loading-label) {
  color: transparent;
}

.v-btn--info.v-btn--loading .v-btn__icon * {
  stroke: transparent;
}

.v-btn--info .v-loading-dots > div {
  background-color: var(--color-text-light);
}

.v-btn--info .v-btn__icon * {
  stroke: var(--color-text-light);
}

.v-btn--hover-info:hover:not(.v-btn--loading, :disabled) {
  background-color: var(--color-grey-40);
  border-color: var(--color-grey-40);
}

.v-btn--hover-info:hover:not(.v-btn--loading, :disabled) .v-btn__label {
  color: var(--color-text-light);
}

.v-btn--hover-info:hover:not(.v-btn--loading, :disabled) .v-btn__icon * {
  stroke: var(--color-text-light);
}

.v-btn--icon {
  background-color: transparent;
  border-color: transparent;
}

.v-btn--icon:not(:disabled):not(.v-btn--loading, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover {
  background-color: var(--color-blue-1);
  border-color: var(--color-blue-1);
}

.v-btn--icon:not(:disabled):not(.v-btn--loading):active {
  box-shadow: inset 0 0 6px 0px rgb(0 0 0 / 10%);
}

.v-btn--icon.v-btn--loading .v-btn__label:not(.v-btn__loading-label) {
  color: transparent;
}

.v-btn--icon.v-btn--loading .v-btn__icon * {
  stroke: transparent;
}

.v-btn--hover-icon:hover:not(.v-btn--loading, :disabled) {
  background-color: transparent;
  border-color: transparent;
}

.v-btn--link {
  background-color: transparent;
  border-color: transparent;
}

.v-btn--link:not(:disabled) .v-btn__label {
  color: var(--color-primary);
}

.v-btn--link:not(:disabled):not(.v-btn--loading, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover {
  background-color: transparent;
  border-color: transparent;
}

.v-btn--link:not(:disabled):not(.v-btn--loading):active {
  box-shadow: inset 0 0 6px 0px transparent;
}

.v-btn--link.v-btn--loading .v-btn__label:not(.v-btn__loading-label) {
  color: transparent;
}

.v-btn--link.v-btn--loading .v-btn__icon * {
  stroke: transparent;
}

.v-btn--link .v-loading-dots > div {
  background-color: var(--color-primary);
}

.v-btn--link .v-btn__icon * {
  stroke: var(--color-primary);
}

.v-btn--hover-link:hover:not(.v-btn--loading, :disabled) {
  background-color: transparent;
  border-color: transparent;
}

.v-btn--hover-link:hover:not(.v-btn--loading, :disabled) .v-btn__label {
  color: var(--color-primary);
}

.v-btn--hover-link:hover:not(.v-btn--loading, :disabled) .v-btn__icon * {
  stroke: var(--color-primary);
}

.v-btn--link,
.v-btn--icon {
  padding: 0px;
}

.v-btn--link:disabled,
.v-btn--icon:disabled {
  background-color: transparent;
  border-color: transparent;
}

.v-btn--link:disabled .v-btn__label,
.v-btn--icon:disabled .v-btn__label {
  color: var(--color-grey-20);
}

.v-btn--link:disabled .v-btn__icon *,
.v-btn--icon:disabled .v-btn__icon * {
  stroke: var(--color-grey-20);
}

.v-btn--link:hover .v-btn__label {
  text-decoration: underline;
}

.v-btn--plain {
  background-color: transparent;
  border: var(--border);
}

.v-btn--plain .v-btn__label {
  color: var(--color-grey-40);
}

.v-btn--plain .v-btn__icon * {
  stroke: var(--color-grey-40);
}

.v-btn--plain.v-btn--primary:not(:disabled) {
  border-color: var(--color-primary);
}

.v-btn--plain.v-btn--primary:not(:disabled) .v-btn__label {
  color: var(--color-primary);
}

.v-btn--plain.v-btn--primary:not(:disabled):not(.v-btn--loading, :disabled, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover .v-btn__label {
  color: var(--color-text-light);
}

.v-btn--plain.v-btn--primary:not(:disabled):not(.v-btn--loading, :disabled, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover .v-btn__icon * {
  stroke: var(--color-text-light);
}

.v-btn--plain.v-btn--primary:not(:disabled).v-btn--loading .v-btn__label {
  color: transparent;
}

.v-btn--plain.v-btn--primary:not(:disabled).v-btn--loading .v-btn__icon * {
  stroke: transparent;
}

.v-btn--plain.v-btn--primary:not(:disabled) .v-loading-dots > div {
  background-color: var(--color-primary);
}

.v-btn--plain.v-btn--primary:not(:disabled) .v-btn__icon * {
  stroke: var(--color-primary);
}

.v-btn--plain.v-btn--secondary:not(:disabled) {
  border-color: var(--color-blue-3);
}

.v-btn--plain.v-btn--secondary:not(:disabled) .v-btn__label {
  color: var(--color-blue-3);
}

.v-btn--plain.v-btn--secondary:not(:disabled):not(.v-btn--loading, :disabled, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover .v-btn__label {
  color: var(--color-primary);
}

.v-btn--plain.v-btn--secondary:not(:disabled):not(.v-btn--loading, :disabled, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover .v-btn__icon * {
  stroke: var(--color-primary);
}

.v-btn--plain.v-btn--secondary:not(:disabled).v-btn--loading .v-btn__label {
  color: transparent;
}

.v-btn--plain.v-btn--secondary:not(:disabled).v-btn--loading .v-btn__icon * {
  stroke: transparent;
}

.v-btn--plain.v-btn--secondary:not(:disabled) .v-loading-dots > div {
  background-color: var(--color-blue-3);
}

.v-btn--plain.v-btn--secondary:not(:disabled) .v-btn__icon * {
  stroke: var(--color-blue-3);
}

.v-btn--plain.v-btn--tertiary:not(:disabled) {
  border-color: var(--color-tertiary);
}

.v-btn--plain.v-btn--tertiary:not(:disabled) .v-btn__label {
  color: var(--color-tertiary);
}

.v-btn--plain.v-btn--tertiary:not(:disabled):not(.v-btn--loading, :disabled, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover .v-btn__label {
  color: var(--color-text-primary);
}

.v-btn--plain.v-btn--tertiary:not(:disabled):not(.v-btn--loading, :disabled, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover .v-btn__icon * {
  stroke: var(--color-text-primary);
}

.v-btn--plain.v-btn--tertiary:not(:disabled).v-btn--loading .v-btn__label {
  color: transparent;
}

.v-btn--plain.v-btn--tertiary:not(:disabled).v-btn--loading .v-btn__icon * {
  stroke: transparent;
}

.v-btn--plain.v-btn--tertiary:not(:disabled) .v-loading-dots > div {
  background-color: var(--color-tertiary);
}

.v-btn--plain.v-btn--tertiary:not(:disabled) .v-btn__icon * {
  stroke: var(--color-tertiary);
}

.v-btn--plain.v-btn--quaternary:not(:disabled) {
  border-color: var(--color-secondary);
}

.v-btn--plain.v-btn--quaternary:not(:disabled) .v-btn__label {
  color: var(--color-secondary);
}

.v-btn--plain.v-btn--quaternary:not(:disabled):not(.v-btn--loading, :disabled, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover .v-btn__label {
  color: var(--color-text-light);
}

.v-btn--plain.v-btn--quaternary:not(:disabled):not(.v-btn--loading, :disabled, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover .v-btn__icon * {
  stroke: var(--color-text-light);
}

.v-btn--plain.v-btn--quaternary:not(:disabled).v-btn--loading .v-btn__label {
  color: transparent;
}

.v-btn--plain.v-btn--quaternary:not(:disabled).v-btn--loading .v-btn__icon * {
  stroke: transparent;
}

.v-btn--plain.v-btn--quaternary:not(:disabled) .v-loading-dots > div {
  background-color: var(--color-secondary);
}

.v-btn--plain.v-btn--quaternary:not(:disabled) .v-btn__icon * {
  stroke: var(--color-secondary);
}

.v-btn--plain.v-btn--red:not(:disabled) {
  border-color: var(--color-red);
}

.v-btn--plain.v-btn--red:not(:disabled) .v-btn__label {
  color: var(--color-red);
}

.v-btn--plain.v-btn--red:not(:disabled):not(.v-btn--loading, :disabled, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover .v-btn__label {
  color: var(--color-text-light);
}

.v-btn--plain.v-btn--red:not(:disabled):not(.v-btn--loading, :disabled, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover .v-btn__icon * {
  stroke: var(--color-text-light);
}

.v-btn--plain.v-btn--red:not(:disabled).v-btn--loading .v-btn__label {
  color: transparent;
}

.v-btn--plain.v-btn--red:not(:disabled).v-btn--loading .v-btn__icon * {
  stroke: transparent;
}

.v-btn--plain.v-btn--red:not(:disabled) .v-loading-dots > div {
  background-color: var(--color-red);
}

.v-btn--plain.v-btn--red:not(:disabled) .v-btn__icon * {
  stroke: var(--color-red);
}

.v-btn--plain.v-btn--green:not(:disabled) {
  border-color: var(--color-green);
}

.v-btn--plain.v-btn--green:not(:disabled) .v-btn__label {
  color: var(--color-green);
}

.v-btn--plain.v-btn--green:not(:disabled):not(.v-btn--loading, :disabled, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover .v-btn__label {
  color: var(--color-text-light);
}

.v-btn--plain.v-btn--green:not(:disabled):not(.v-btn--loading, :disabled, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover .v-btn__icon * {
  stroke: var(--color-text-light);
}

.v-btn--plain.v-btn--green:not(:disabled).v-btn--loading .v-btn__label {
  color: transparent;
}

.v-btn--plain.v-btn--green:not(:disabled).v-btn--loading .v-btn__icon * {
  stroke: transparent;
}

.v-btn--plain.v-btn--green:not(:disabled) .v-loading-dots > div {
  background-color: var(--color-green);
}

.v-btn--plain.v-btn--green:not(:disabled) .v-btn__icon * {
  stroke: var(--color-green);
}

.v-btn--plain.v-btn--white:not(:disabled) {
  border-color: var(--color-white);
}

.v-btn--plain.v-btn--white:not(:disabled) .v-btn__label {
  color: var(--color-white);
}

.v-btn--plain.v-btn--white:not(:disabled):not(.v-btn--loading, :disabled, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover .v-btn__label {
  color: var(--color-text-primary);
}

.v-btn--plain.v-btn--white:not(:disabled):not(.v-btn--loading, :disabled, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover .v-btn__icon * {
  stroke: var(--color-text-primary);
}

.v-btn--plain.v-btn--white:not(:disabled).v-btn--loading .v-btn__label {
  color: transparent;
}

.v-btn--plain.v-btn--white:not(:disabled).v-btn--loading .v-btn__icon * {
  stroke: transparent;
}

.v-btn--plain.v-btn--white:not(:disabled) .v-loading-dots > div {
  background-color: var(--color-white);
}

.v-btn--plain.v-btn--white:not(:disabled) .v-btn__icon * {
  stroke: var(--color-white);
}

.v-btn--plain.v-btn--info:not(:disabled) {
  border-color: var(--color-grey-40);
}

.v-btn--plain.v-btn--info:not(:disabled) .v-btn__label {
  color: var(--color-grey-40);
}

.v-btn--plain.v-btn--info:not(:disabled):not(.v-btn--loading, :disabled, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover .v-btn__label {
  color: var(--color-text-light);
}

.v-btn--plain.v-btn--info:not(:disabled):not(.v-btn--loading, :disabled, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover .v-btn__icon * {
  stroke: var(--color-text-light);
}

.v-btn--plain.v-btn--info:not(:disabled).v-btn--loading .v-btn__label {
  color: transparent;
}

.v-btn--plain.v-btn--info:not(:disabled).v-btn--loading .v-btn__icon * {
  stroke: transparent;
}

.v-btn--plain.v-btn--info:not(:disabled) .v-loading-dots > div {
  background-color: var(--color-grey-40);
}

.v-btn--plain.v-btn--info:not(:disabled) .v-btn__icon * {
  stroke: var(--color-grey-40);
}

.v-btn--plain.v-btn--icon:not(:disabled) {
  border-color: transparent;
}

.v-btn--plain.v-btn--icon:not(:disabled) .v-btn__label {
  color: transparent;
}

.v-btn--plain.v-btn--icon:not(:disabled).v-btn--loading .v-btn__label {
  color: transparent;
}

.v-btn--plain.v-btn--icon:not(:disabled).v-btn--loading .v-btn__icon * {
  stroke: transparent;
}

.v-btn--plain.v-btn--icon:not(:disabled) .v-loading-dots > div {
  background-color: transparent;
}

.v-btn--plain.v-btn--icon:not(:disabled) .v-btn__icon * {
  stroke: transparent;
}

.v-btn--plain.v-btn--link:not(:disabled) {
  border-color: transparent;
}

.v-btn--plain.v-btn--link:not(:disabled) .v-btn__label {
  color: transparent;
}

.v-btn--plain.v-btn--link:not(:disabled):not(.v-btn--loading, :disabled, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover .v-btn__label {
  color: var(--color-primary);
}

.v-btn--plain.v-btn--link:not(:disabled):not(.v-btn--loading, :disabled, [class^='v-btn--hover-'], [class*=' v-btn--hover-']):hover .v-btn__icon * {
  stroke: var(--color-primary);
}

.v-btn--plain.v-btn--link:not(:disabled).v-btn--loading .v-btn__label {
  color: transparent;
}

.v-btn--plain.v-btn--link:not(:disabled).v-btn--loading .v-btn__icon * {
  stroke: transparent;
}

.v-btn--plain.v-btn--link:not(:disabled) .v-loading-dots > div {
  background-color: transparent;
}

.v-btn--plain.v-btn--link:not(:disabled) .v-btn__icon * {
  stroke: transparent;
}

.v-btn--plain.v-btn--secondary:not(:disabled) {
  border-color: var(--color-blue-5);
}

.v-btn--plain.v-btn--secondary:not(:disabled) .v-btn__label {
  color: var(--color-blue-5);
}

.v-btn--plain.v-btn--secondary:not(:disabled) .v-btn__icon * {
  stroke: var(--color-blue-5);
}

.v-btn--plain.v-btn--link:not(:disabled) {
  background-color: transparent;
  border-color: transparent;
}

.v-btn--plain.v-btn--link:not(:disabled) .v-btn__label {
  color: var(--color-primary);
}

.v-btn--plain.v-btn--link:not(:disabled) .v-btn__icon * {
  stroke: var(--color-primary);
}

.v-btn--plain:disabled {
  border-color: var(--color-grey-40);
  background-color: transparent;
}

.v-btn--plain:disabled .v-btn__label {
  color: var(--color-grey-40);
}

.v-btn--plain:disabled .v-btn__icon * {
  stroke: var(--color-grey-40);
}

.v-btn--pulse {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  70% {
    box-shadow: 0 0 0 5px rgba(0, 0, 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(0, 0, 0);
  }
}

.v-btn--pulse.v-btn--primary {
  animation: pulse-primary 2s infinite;
  box-shadow: 0 0 0 0 rgba(var(--color-primary--rgb));
}

@keyframes pulse-primary {
  70% {
    box-shadow: 0 0 0 5px rgba(var(--color-primary--rgb), 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(var(--color-primary--rgb), 0);
  }
}

.v-btn--pulse.v-btn--secondary {
  animation: pulse-secondary 2s infinite;
  box-shadow: 0 0 0 0 rgba(var(--color-blue-5--rgb));
}

@keyframes pulse-secondary {
  70% {
    box-shadow: 0 0 0 5px rgba(var(--color-blue-5--rgb), 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(var(--color-blue-5--rgb), 0);
  }
}

.v-btn--pulse.v-btn--tertiary {
  animation: pulse-tertiary 2s infinite;
  box-shadow: 0 0 0 0 rgba(var(--color-tertiary--rgb));
}

@keyframes pulse-tertiary {
  70% {
    box-shadow: 0 0 0 5px rgba(var(--color-tertiary--rgb), 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(var(--color-tertiary--rgb), 0);
  }
}

.v-btn--pulse.v-btn--quaternary {
  animation: pulse-quaternary 2s infinite;
  box-shadow: 0 0 0 0 rgba(var(--color-secondary--rgb));
}

@keyframes pulse-quaternary {
  70% {
    box-shadow: 0 0 0 5px rgba(var(--color-secondary--rgb), 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(var(--color-secondary--rgb), 0);
  }
}

.v-btn--pulse.v-btn--red {
  animation: pulse-red 2s infinite;
  box-shadow: 0 0 0 0 rgba(var(--color-red--rgb));
}

@keyframes pulse-red {
  70% {
    box-shadow: 0 0 0 5px rgba(var(--color-red--rgb), 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(var(--color-red--rgb), 0);
  }
}

.v-btn--pulse.v-btn--green {
  animation: pulse-green 2s infinite;
  box-shadow: 0 0 0 0 rgba(var(--color-green--rgb));
}

@keyframes pulse-green {
  70% {
    box-shadow: 0 0 0 5px rgba(var(--color-green--rgb), 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(var(--color-green--rgb), 0);
  }
}

.v-btn--pulse.v-btn--white {
  animation: pulse-white 2s infinite;
  box-shadow: 0 0 0 0 rgba(var(--color-white--rgb));
}

@keyframes pulse-white {
  70% {
    box-shadow: 0 0 0 5px rgba(var(--color-grey-12--rgb), 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(var(--color-grey-12--rgb), 0);
  }
}

.v-btn--pulse.v-btn--info {
  animation: pulse-info 2s infinite;
  box-shadow: 0 0 0 0 rgba(var(--color-grey-40--rgb), 0.7);
}

@keyframes pulse-info {
  70% {
    box-shadow: 0 0 0 5px rgba(var(--color-grey-40--rgb), 0);
  }

  100% {
    box-shadow: 0 0 0 0 rgba(var(--color-grey-40--rgb), 0);
  }
}

.v-btn--pulse:disabled {
  box-shadow: 0 0 0 0 rgba(var(--color-grey-40--rgb), 0.7);
}

.v-btn--rounded {
  border-radius: 50px;
}

.v-btn__label {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  line-height: 1;
}
</style>
